import { useContext, useMemo, useState, useEffect } from "react";
import { Table, TableBody } from "@/components/ui/table";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
} from "@tanstack/react-table";
import { SortableTableHeader } from "@/components/table/table-header";
import { Pagination } from "@/components/table/pagination";
import { SkeletonTableBody } from "@/components/ui/skeleton-table-body";
import { DashboardContext } from "@/components/dashboard-layout";
import { DivergenceTableBody } from "./divergences-table-body";
import { DivergenceReportDetails } from "./divergence-details";
import { columns } from "./columns";
import { DivergencesFilterToolbar } from "./divergences-filter-toolbar";

export interface DivergenceReport {
  id: number;
  username: string;
  userAgent: string;
  service: string;
  callId: string;
  caller: string;
  created: string;
  reason: string;
  message: string;
}

export interface DivergenceReportsTableProps {
  divergenceReports: DivergenceReport[];
  isLoading: boolean;
  defaultDivergenceId?: number;
  defaultSearch?: string;
  defaultPage?: number;
  defaultPageSize?: number;
  after?: string;
  before?: string;
  onNavigate?: (params: {
    search?: {
      divergenceId?: number;
      search?: string;
      after?: string;
      before?: string;
      page?: number;
      pageSize?: number;
    };
  }) => void;
  setAfter: (after: string) => void;
  setBefore: (before: string) => void;
}

export function DivergenceReportsTable({
  divergenceReports,
  isLoading,
  defaultDivergenceId,
  defaultSearch = "",
  setAfter,
  setBefore,
  after,
  before,
  defaultPage,
  defaultPageSize,
  onNavigate,
}: DivergenceReportsTableProps) {
  const context = useContext(DashboardContext);

  const [search, setSearch] = useState(defaultSearch);

  const [currentSidebarId, setCurrentSidebarId] = useState<number | null>(null);

  const [pagination, setPagination] = useState({
    pageIndex: defaultPage && defaultPage > 0 ? defaultPage - 1 : 0,
    pageSize: defaultPageSize || 10,
  });

  useEffect(() => {
    if (defaultDivergenceId !== currentSidebarId) {
      if (defaultDivergenceId && divergenceReports.length >= 0) {
        const reportId = defaultDivergenceId;
        if (reportId >= 0) {
          const report = divergenceReports.find((r) => r.id === reportId);
          if (report && context?.setRightSidebar) {
            context.setRightSidebar(<DivergenceReportDetails id={report.id} />);
            context.setRightSidebarParamKey?.("divergenceId");
            setCurrentSidebarId(defaultDivergenceId);
          }
        }
      } else if (!defaultDivergenceId && context?.setRightSidebar) {
        context.setRightSidebar(null);
        setCurrentSidebarId(null);
      }
    }
  }, [defaultDivergenceId, divergenceReports.length, currentSidebarId]);

  useEffect(() => {
    setSearch(defaultSearch);
  }, [defaultSearch]);

  useEffect(() => {
    setPagination({
      pageIndex: defaultPage && defaultPage > 0 ? defaultPage - 1 : 0,
      pageSize: defaultPageSize || 10,
    });
  }, [defaultPage, defaultPageSize]);

  useEffect(() => {
    const currentPage = pagination.pageIndex + 1;
    const shouldIncludePage = currentPage > 1;
    const shouldIncludePageSize = pagination.pageSize !== 10;

    const urlPage = shouldIncludePage ? currentPage : undefined;
    const urlPageSize = shouldIncludePageSize ? pagination.pageSize : undefined;

    const needsUpdate = defaultPage !== urlPage || defaultPageSize !== urlPageSize;

    if (needsUpdate) {
      setTimeout(() => {
        onNavigate?.({
          search: {
            divergenceId: defaultDivergenceId || undefined,
            search: search || undefined,
            after: after || undefined,
            before: before || undefined,
            page: urlPage,
            pageSize: urlPageSize,
          },
        });
      }, 0);
    }
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    defaultDivergenceId,
    search,
    after,
    before,
    defaultPage,
    defaultPageSize,
    onNavigate,
  ]);

  const filteredReports = useMemo(() => {
    let data = divergenceReports;

    if (search.trim()) {
      const lower = search.toLowerCase();
      data = data.filter(
        (r) =>
          r.username.toLowerCase().includes(lower) ||
          r.userAgent.toLowerCase().includes(lower) ||
          r.service.toLowerCase().includes(lower) ||
          r.callId.toLowerCase().includes(lower) ||
          r.caller.toLowerCase().includes(lower) ||
          r.reason.toLowerCase().includes(lower) ||
          r.message.toLowerCase().includes(lower)
      );
    }

    return data;
  }, [divergenceReports, search]);

  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data: filteredReports,
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    autoResetPageIndex: false,
    manualPagination: false,
  });

  const handleRowClick = (id: number) => {
    const report = divergenceReports.find((r) => r.id === id);
    if (report && context?.setRightSidebar) {
      context.setRightSidebar(<DivergenceReportDetails id={report.id} />);
      context.setRightSidebarParamKey?.("divergenceId");

      setTimeout(() => {
        const currentPage = pagination.pageIndex + 1;
        const shouldIncludePage = currentPage > 1;
        const shouldIncludePageSize = pagination.pageSize !== 10;

        onNavigate?.({
          search: {
            divergenceId: id,
            search: search || undefined,
            after: after || undefined,
            before: before || undefined,
            page: shouldIncludePage ? currentPage : undefined,
            pageSize: shouldIncludePageSize ? pagination.pageSize : undefined,
          },
        });
      }, 0);
    }
  };

  const handleFilterChange = (filters: { search?: string; from?: string; to?: string }) => {
    const newSearch = filters.search ?? "";
    const newAfter = filters.from ?? "";
    const newBefore = filters.to ?? "";

    setSearch(newSearch);
    setAfter(newAfter);
    setBefore(newBefore);

    if (newSearch !== defaultSearch || newAfter !== after || newBefore !== before) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }

    setTimeout(() => {
      const shouldIncludePageSize = pagination.pageSize !== 10;

      onNavigate?.({
        search: {
          divergenceId: defaultDivergenceId || undefined,
          search: newSearch || undefined,
          after: newAfter || undefined,
          before: newBefore || undefined,
          page: undefined,
          pageSize: shouldIncludePageSize ? pagination.pageSize : undefined,
        },
      });
    }, 0);
  };

  return (
    <div>
      <DivergencesFilterToolbar
        onFilterChange={handleFilterChange}
        defaultSearch={search}
        defaultFrom={after}
        defaultTo={before}
      />
      <Table className="border border-gray-300">
        <SortableTableHeader table={table} />
        <TableBody>
          {isLoading ? (
            <SkeletonTableBody pageSize={10} columnsLength={columns.length} />
          ) : (
            <DivergenceTableBody table={table} onRowClick={handleRowClick} selectedId={defaultDivergenceId} />
          )}
        </TableBody>
      </Table>
      <Pagination table={table} />
    </div>
  );
}
